/**
 * One conversation as turns. Your message sits on the right; under it the reply: its activity
 * (plan or steps, shut once done), steps waiting for you as approval cards, then its answer
 * as Markdown, streamed while it is written. Each answer has its actions (copy, try again,
 * read aloud) and what it took (time, tokens, cost, model, context). Days are divided, errors
 * say what to do next, and a stopped reply offers to continue.
 */
import { ChevronDown, Gauge, KeyRound, Pencil, RefreshCw, Square, TriangleAlert, Volume2 } from '@orgus/ui/icons';
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { AssistantEntry, AssistantLive, AssistantReply, Conversation } from '@orgus/protocol';
import {
  Button,
  CopyButton,
  IconButton,
  Markdown,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Pressable,
  Tooltip,
  cn,
  formatAbsolute,
  formatClock,
  formatCompactNumber,
  formatDate,
  formatDuration,
  formatInt,
  formatMoney,
  formatPercent,
  formatTokens,
  Spinner,
} from '@orgus/ui';
import { Activity, hasActivity } from './activity/Activity.tsx';
import { ApprovalStep } from './ApprovalStep.tsx';
import { buildTurns } from './turns.ts';
import type { Turn } from './turns.ts';

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/** Three dots and "Thinking": before a reply has written anything. */
export function WaitIndicator({ label = 'Thinking' }: { label?: string }) {
  return (
    <div role="status" aria-label="The assistant is working" className="flex items-center gap-2 text-ui text-fg-muted">
      <Spinner size={14} className="text-fg-subtle" />
      {label}…
    </div>
  );
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "Today", "Yesterday", a weekday this week, else a date: between turns on different days. */
export function dayLabel(ts: number, now: number): string {
  const d = new Date(ts);
  const n = new Date(now);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(n) - day(d)) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff > 1 && diff < 7) return WEEKDAYS[d.getDay()]!;
  return formatDate(ts, now);
}

/** Long messages fold: past 9 lines they show 6 and a way to open the rest. */
function FoldedText({ text }: { text: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [long, setLong] = useState(false);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const line = parseFloat(getComputedStyle(el).lineHeight) || 20;
    setLong(el.scrollHeight > line * 9 + 1);
  }, [text]);
  return (
    <div className="flex flex-col items-start gap-1">
      <p ref={ref} dir="auto" className={cn('whitespace-pre-wrap break-words', long && !open && 'max-h-30 overflow-hidden')}>
        {text}
      </p>
      {long && (
        <Pressable onClick={() => setOpen((o) => !o)} className="text-caption font-medium text-accent-text hover:underline">
          {open ? 'Show less' : 'Show more'}
        </Pressable>
      )}
    </div>
  );
}

function UserMessage({ entry, onEdit, canEdit }: { entry: AssistantEntry; onEdit?: (text: string) => void; canEdit: boolean }) {
  const text = entry.text ?? '';
  return (
    <article className="group/msg flex flex-col items-end gap-1" aria-label={`You said, ${formatClock(entry.at)}`} data-turn-question={entry.id}>
      <div className="motion-arrive max-w-xl rounded-overlay border border-border-subtle bg-raised px-4 py-3 text-body text-fg">
        <FoldedText text={text} />
      </div>
      <div className="flex h-6 items-center gap-1 text-caption text-fg-subtle opacity-0 transition-opacity duration-(--duration-fast) focus-within:opacity-100 group-hover/msg:opacity-100">
        {entry.by && entry.by !== 'you' && <span>{entry.by}</span>}
        <Tooltip content={formatAbsolute(entry.at)}>
          <span className="shrink-0 whitespace-nowrap num">{formatClock(entry.at)}</span>
        </Tooltip>
        <CopyButton text={text} label="Copy your message" />
        {onEdit && <IconButton size="xs" label={canEdit ? 'Edit and send again' : 'Edit once the reply finishes'} icon={<Pencil />} disabled={!canEdit} onClick={() => onEdit(text)} />}
      </div>
    </article>
  );
}

/** "13s · 27.4K tokens", opening what the reply took. */
function ReplyStats({ reply }: { reply: AssistantReply }) {
  const tokens = reply.tokens ? reply.tokens.input + reply.tokens.output + reply.tokens.cacheRead + reply.tokens.cacheWrite : null;
  const parts = [reply.durationMs != null ? formatDuration(reply.durationMs) : null, tokens ? formatTokens(tokens) : null].filter(Boolean);
  if (!parts.length) return null;
  const rows: [string, string][] = [];
  if (reply.model) rows.push(['Model', reply.model]);
  if (reply.durationMs != null) rows.push(['Whole reply', formatDuration(reply.durationMs)]);
  if (reply.turns != null) rows.push(['Model turns', formatInt(reply.turns)]);
  if (reply.tokens) {
    rows.push(['Tokens in', formatInt(reply.tokens.input)]);
    rows.push(['Tokens out', formatInt(reply.tokens.output)]);
    rows.push(['Read from cache', formatInt(reply.tokens.cacheRead)]);
    rows.push(['Written to cache', formatInt(reply.tokens.cacheWrite)]);
  }
  if (reply.context?.used) rows.push(['Context in use', reply.context.size ? `${formatCompactNumber(reply.context.used)} of ${formatCompactNumber(reply.context.size)} (${formatPercent(reply.context.used / reply.context.size)})` : formatTokens(reply.context.used)]);
  if (reply.cost != null) rows.push(['Cost', formatMoney(reply.cost)]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-caption text-fg-subtle" icon={<Gauge />} aria-label={`What this reply took: ${parts.join(', ')}`}>
          {/* On a phone only the time; the tokens are one tap away in the popover. */}
          <span className="num">
            {parts[0]}
            {parts[1] && <span className="hidden sm:inline">{` · ${parts[1]}`}</span>}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        <p className="mb-2 text-ui font-medium text-fg">What this reply took</p>
        <dl className="flex flex-col gap-1 text-caption">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4">
              <dt className="text-fg-subtle">{k}</dt>
              <dd className="num text-right text-fg">{v}</dd>
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  );
}

/** Read the answer aloud with the browser's own voice. Hidden where there is none. */
function ReadAloud({ text }: { text: string }) {
  const [speaking, setSpeaking] = useState(false);
  const can = typeof window !== 'undefined' && 'speechSynthesis' in window;
  useEffect(() => () => void (speaking && window.speechSynthesis.cancel()), [speaking]);
  if (!can) return null;
  const plain = text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[|#*_`>]+/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  const toggle = () => {
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const u = new SpeechSynthesisUtterance(plain);
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };
  return <IconButton size="xs" label={speaking ? 'Stop reading' : 'Read aloud'} icon={speaking ? <Square /> : <Volume2 />} pressed={speaking} onClick={toggle} />;
}

// ---------------------------------------------------------------------------
// A turn
// ---------------------------------------------------------------------------

export interface ThreadActions {
  /** Send this text as a new message (try again, continue). */
  onResend?: (text: string) => void;
  /** Put this text in the composer to edit and send. */
  onEdit?: (text: string) => void;
}

function TurnView({ turn, conversationId, live, newest, busy, actions, installing }: { turn: Turn; conversationId: string; live: AssistantLive | null; newest: boolean; busy: boolean; actions: ThreadActions; installing?: string | null }) {
  const navigate = useNavigate();
  const streamingText = turn.live && live?.runId === turn.runId ? live.text : '';
  const answer = turn.answer || (turn.live ? '' : '');
  const showActivity = hasActivity(turn) || (turn.live && !!live?.thinking);
  const stopped = turn.notes.some((n) => /^Stopped by /.test(n.text ?? '')) || turn.reply?.outcome === 'stopped';
  const failed = turn.errors[turn.errors.length - 1];
  const nothingYet = turn.live && !showActivity && !answer && !streamingText;
  const question = turn.user?.text ?? '';
  return (
    <div className="flex flex-col gap-3" data-turn={turn.key}>
      {turn.user && <UserMessage entry={turn.user} onEdit={actions.onEdit} canEdit={!busy} />}
      <article className="motion-arrive group/answer flex min-w-0 flex-col gap-3" aria-label={`The assistant said${turn.lastAt ? `, ${formatClock(turn.lastAt)}` : ''}`}>
        {installing ? <WaitIndicator label={`Installing Claude Code on ${installing}`} /> : nothingYet && <WaitIndicator />}
        {showActivity && <Activity turn={turn} live={turn.live ? live : null} />}
        {turn.waiting.map((n) => (
          <ApprovalStep key={n.entry.id} conversationId={conversationId} entry={n.entry} />
        ))}
        {streamingText && !answer && <Markdown source={streamingText} streaming className="text-body" />}
        {answer && <Markdown source={answer} streaming={turn.live && !!streamingText} className="text-body" />}
        {streamingText && answer && <Markdown source={streamingText} streaming className="text-body" />}
        {turn.notes
          .filter((n) => !/^Stopped by /.test(n.text ?? ''))
          .map((n) => (
            <p key={n.id} className="flex items-center gap-2 text-caption text-fg-subtle">
              <span className="h-px flex-1 bg-border-subtle" aria-hidden />
              {n.text}
              <span className="h-px flex-1 bg-border-subtle" aria-hidden />
            </p>
          ))}
        {failed && (
          <div role="alert" className="flex items-start gap-3 rounded-card border border-status-danger-line bg-status-danger-soft px-3 py-2">
            <TriangleAlert className="mt-1 size-4 shrink-0 text-status-danger" aria-hidden />
            {/* The button drops under the words when the row is narrow. */}
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <p className="min-w-0 flex-1 basis-48 text-ui text-fg">{failed.text}</p>
              {failed.code === 'no-account' && (
                <Button variant="primary" size="sm" icon={<KeyRound />} onClick={() => navigate('/settings/accounts')}>
                  Add an account
                </Button>
              )}
              {newest && question && actions.onResend && (
                <Button variant="secondary" size="sm" icon={<RefreshCw />} disabled={busy} onClick={() => actions.onResend!(question)}>
                  Send it again
                </Button>
              )}
            </div>
          </div>
        )}
        {stopped && (
          <p className="flex items-center gap-2 text-caption text-fg-subtle">
            You stopped this reply.
            {newest && actions.onResend && !busy && (
              <Pressable onClick={() => actions.onResend!('Continue')} className="font-medium text-accent-text hover:underline">
                Continue
              </Pressable>
            )}
          </p>
        )}
        {!turn.live && (answer || turn.reply) && (
          <div
            role="toolbar"
            aria-label="Actions for this answer"
            className={cn('-ml-1 flex h-6 items-center gap-1 text-caption text-fg-subtle', !newest && 'opacity-0 transition-opacity duration-(--duration-fast) focus-within:opacity-100 group-hover/answer:opacity-100')}
          >
            {answer && <CopyButton text={answer} label="Copy the answer" />}
            {newest && question && actions.onResend && <IconButton size="xs" label={busy ? 'Try again once this reply finishes' : 'Try a different answer'} icon={<RefreshCw />} disabled={busy} onClick={() => actions.onResend!(question)} />}
            {answer && <ReadAloud text={answer} />}
            <Tooltip content={formatAbsolute(turn.lastAt)}>
              <span className="ml-1 shrink-0 whitespace-nowrap num">{formatClock(turn.lastAt)}</span>
            </Tooltip>
            <span className="ml-auto">{turn.reply && <ReplyStats reply={turn.reply} />}</span>
          </div>
        )}
      </article>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The thread
// ---------------------------------------------------------------------------

/** The thread of one conversation, oldest first. */
export function Thread({ conversation, live, actions = {}, now = Date.now() }: { conversation: Conversation; live: AssistantLive | null; actions?: ThreadActions; now?: number }) {
  const turns = useMemo(() => buildTurns(conversation), [conversation]);
  const busy = conversation.state === 'running' || conversation.state === 'waiting';
  return (
    <div className="flex flex-col gap-8">
      {turns.map((t, i) => {
        const prev = turns[i - 1];
        const showDay = i === 0 ? dayLabel(t.at, now) !== 'Today' : dayLabel(prev!.at, now) !== dayLabel(t.at, now);
        return (
          <Fragment key={t.key}>
            {showDay && (
              <p className="text-center text-caption text-fg-subtle" aria-label={new Date(t.at).toDateString()}>
                {dayLabel(t.at, now)}
              </p>
            )}
            <TurnView turn={t} conversationId={conversation.id} live={live} newest={i === turns.length - 1} busy={busy} actions={actions} installing={i === turns.length - 1 ? (conversation.installing?.name ?? null) : null} />
          </Fragment>
        );
      })}
      {conversation.state === 'failed' && conversation.error && !turns[turns.length - 1]?.errors.length && (
        <div role="alert" className="flex items-start gap-3 rounded-card border border-status-danger-line bg-status-danger-soft px-3 py-2">
          <TriangleAlert className="mt-1 size-4 shrink-0 text-status-danger" aria-hidden />
          <p className="min-w-0 flex-1 text-ui text-fg">{conversation.error}</p>
        </div>
      )}
    </div>
  );
}

/** "Jump to latest": shown when the newest is below what you can see. */
export function JumpToLatest({ onJump, unread }: { onJump: () => void; unread: boolean }) {
  return (
    <IconButton
      label={unread ? 'The reply continues below' : 'Jump to latest'}
      icon={<ChevronDown />}
      variant="secondary"
      size="md"
      className={cn('rounded-pill shadow-elevation-2', unread && 'ring-2 ring-accent')}
      onClick={onJump}
    />
  );
}
